import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Home, LogOut, Building2, CheckCircle, Clock, FileText,
  Loader2, Phone, Mail, User, Upload, Calendar, MessageSquare,
  AlertCircle, Download, ExternalLink, Target, Shield, Wallet, Bell
} from "lucide-react";
import { API } from "@/App";

// MilestoneItem component - defined outside to prevent re-creation on each render
const MilestoneItem = ({ label, completed, date, current }) => (
  <div className={`flex items-center gap-3 p-4 rounded-lg border ${
    completed ? "bg-green-50 border-green-200" : 
    current ? "bg-blue-50 border-blue-200" :
    "bg-slate-50 border-slate-200"
  }`}>
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
      completed ? "bg-green-500 text-white" : 
      current ? "bg-blue-500 text-white" :
      "bg-slate-200 text-slate-400"
    }`}>
      {completed ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
    </div>
    <div className="flex-1">
      <p className={`font-medium ${
        completed ? "text-green-700" : 
        current ? "text-blue-700" :
        "text-slate-500"
      }`}>{label}</p>
      {date && <p className="text-xs text-slate-500">{new Date(date).toLocaleDateString('en-GB')}</p>}
      {current && !completed && <p className="text-xs text-blue-600 font-medium">Current Stage</p>}
    </div>
  </div>
);

export const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  // NEW: Progression state for 18-step milestones
  const [progression, setProgression] = useState(null);
  const [loadingProgression, setLoadingProgression] = useState(false);
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Support both main auth and portal auth
  const token = localStorage.getItem("token") || localStorage.getItem("portal_token");
  const user = JSON.parse(localStorage.getItem("user") || localStorage.getItem("portal_client") || "{}");

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/buyer-portal/notifications`, { headers });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [token]);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/buyer-portal/notifications/${notificationId}/read`, {}, { headers });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  // Mark all as read
  const markAllRead = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/buyer-portal/notifications/mark-all-read`, {}, { headers });
      fetchNotifications();
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all notifications read:", error);
    }
  };

  // Format notification time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-GB");
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchDashboard();
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const notifInterval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(notifInterval);
  }, [token, fetchNotifications]);

  // Fetch progression for the selected purchase
  const fetchProgression = async (caseId) => {
    if (!caseId) return;
    setLoadingProgression(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/portal/buyer-portal/progression/${caseId}`, { headers });
      setProgression(response.data);
    } catch (error) {
      console.error("Error fetching progression:", error);
      // Progression endpoint might not be available for all buyers
    } finally {
      setLoadingProgression(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Try to get buyer's purchases from the sales cases
      const response = await axios.get(`${API}/portal/buyer/dashboard`, { headers });
      setPurchases(response.data.purchases || []);
      if (response.data.purchases?.length > 0) {
        setSelectedPurchase(response.data.purchases[0]);
        
        // Fetch documents for this case
        const caseId = response.data.purchases[0].case?.case_id;
        if (caseId) {
          // Fetch progression data with the new 18-step milestones
          fetchProgression(caseId);
          
          try {
            const docsRes = await axios.get(`${API}/portal/buyer/documents/${caseId}`, { headers });
            setDocuments(docsRes.data?.documents || []);
          } catch (e) {
            console.log("Documents endpoint not available");
          }
          
          // Fetch messages for this case
          try {
            const msgsRes = await axios.get(`${API}/portal/buyer/messages/${caseId}`, { headers });
            setMessages(msgsRes.data?.messages || []);
          } catch (e) {
            console.log("Messages endpoint not available");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching buyer dashboard:", error);
      // If the API doesn't exist yet, show empty state
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", docType);
      formData.append("case_id", selectedPurchase?.case?.case_id);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data"
      };
      
      await axios.post(`${API}/portal/buyer/documents/upload`, formData, { headers });
      toast.success("Document uploaded successfully");
      
      // Refresh documents
      const caseId = selectedPurchase?.case?.case_id;
      if (caseId) {
        const docsRes = await axios.get(`${API}/portal/buyer/documents/${caseId}`, { headers: { Authorization: `Bearer ${token}` } });
        setDocuments(docsRes.data?.documents || []);
      }
    } catch (error) {
      toast.error("Failed to upload document");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Send message handler
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/portal/buyer/messages`, {
        case_id: selectedPurchase?.case?.case_id,
        message: newMessage,
        sender_type: "buyer"
      }, { headers });
      
      toast.success("Message sent");
      setNewMessage("");
      
      // Refresh messages
      const caseId = selectedPurchase?.case?.case_id;
      if (caseId) {
        const msgsRes = await axios.get(`${API}/portal/buyer/messages/${caseId}`, { headers });
        setMessages(msgsRes.data?.messages || []);
      }
    } catch (error) {
      toast.error("Failed to send message");
      console.error(error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Calculate progress percentage
  const calculateProgress = (milestones) => {
    if (!milestones) return 0;
    const totalSteps = 14;
    const completedSteps = Object.values(milestones).filter(v => v === true).length;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_client");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Black Header with Goodacres Logo */}
      <header className="bg-black text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/goodacres-full-logo.jpg" 
                alt="Goodacres" 
                className="h-12 w-auto rounded"
              />
              <div className="border-l border-slate-600 pl-4">
                <h1 className="font-semibold text-lg">Buyer Portal</h1>
                <p className="text-sm text-slate-400">Welcome, {user?.name || 'Buyer'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Notifications Bell */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-white hover:bg-slate-800"
                    data-testid="buyer-notification-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="bg-black text-white px-4 py-3 flex items-center justify-between rounded-t-md">
                    <span className="font-medium">Notifications</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-white text-xs"
                        onClick={markAllRead}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8">
                        <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                            !notif.read ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            markNotificationRead(notif.notification_id);
                            setNotificationsOpen(false);
                          }}
                          data-testid={`buyer-notification-${notif.notification_id}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-2 h-2 rounded-full mt-2 ${
                              !notif.read ? "bg-blue-500" : "bg-transparent"
                            }`} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-800">{notif.message}</p>
                              <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {purchases.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-2xl font-semibold text-slate-700">No Active Purchases</h2>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">
                You don&apos;t have any active property purchases linked to your account yet. 
                Once you have an accepted offer, your purchase details will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Property Summary Card */}
            {selectedPurchase && (
              <>
                <Card className="overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Your Purchase</p>
                        <h2 className="text-2xl font-bold">{selectedPurchase.case?.address_line1 || 'Property Address'}</h2>
                        <p className="text-blue-100">{selectedPurchase.case?.address_line2}</p>
                        <p className="text-blue-100">{selectedPurchase.case?.postcode}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-100 text-sm">Purchase Price</p>
                        <p className="text-3xl font-bold">
                          £{(selectedPurchase.case?.offer_price || selectedPurchase.case?.asking_price || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm text-blue-100 mb-2">
                        <span>Overall Progress</span>
                        <span>{calculateProgress(selectedPurchase.milestones)}% Complete</span>
                      </div>
                      <Progress value={calculateProgress(selectedPurchase.milestones)} className="h-2 bg-blue-500/30" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={
                          selectedPurchase.case?.current_stage === "completed" ? "bg-green-100 text-green-700" :
                          selectedPurchase.case?.current_stage === "exchanged" ? "bg-purple-100 text-purple-700" :
                          selectedPurchase.case?.current_stage === "sstc" ? "bg-blue-100 text-blue-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {selectedPurchase.case?.current_stage?.replace(/_/g, " ").toUpperCase() || "IN PROGRESS"}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          Last updated: {new Date().toLocaleDateString('en-GB')}
                        </span>
                      </div>
                      {selectedPurchase.case?.target_exchange && (
                        <div className="flex items-center gap-2 text-sm">
                          <Target className="w-4 h-4 text-amber-600" />
                          <span className="text-slate-600">Target Exchange: </span>
                          <span className="font-medium">{new Date(selectedPurchase.case.target_exchange).toLocaleDateString('en-GB')}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Tabbed Content */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid grid-cols-4 w-full bg-white border">
                    <TabsTrigger value="overview" className="gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Progress
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="gap-2">
                      <FileText className="w-4 h-4" />
                      Documents
                    </TabsTrigger>
                    <TabsTrigger value="messages" className="gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Messages
                    </TabsTrigger>
                    <TabsTrigger value="contacts" className="gap-2">
                      <User className="w-4 h-4" />
                      Contacts
                    </TabsTrigger>
                  </TabsList>

                  {/* Overview/Progress Tab */}
                  <TabsContent value="overview" className="mt-4 space-y-6" data-testid="buyer-progress-tab">
                    {/* New 18-step Milestones (if available) */}
                    {loadingProgression ? (
                      <Card>
                        <CardContent className="p-8 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                          <span className="text-slate-600">Loading progression...</span>
                        </CardContent>
                      </Card>
                    ) : progression?.milestones?.length > 0 ? (
                      <>
                        {/* Progress Summary */}
                        <Card className="overflow-hidden">
                          <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b">
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-2">
                                <Target className="w-5 h-5 text-blue-600" />
                                Sales Progression
                              </CardTitle>
                              <Badge className="bg-blue-100 text-blue-700 text-lg px-4">
                                {progression.completion_percentage}% Complete
                              </Badge>
                            </div>
                            <CardDescription>Track all 18 steps of your property purchase</CardDescription>
                          </CardHeader>
                          <CardContent className="p-6">
                            <Progress value={progression.completion_percentage} className="h-3 mb-2" />
                            <p className="text-sm text-slate-600">
                              {progression.milestones.filter(m => m.status === "done").length} of {progression.milestones.length} milestones completed
                            </p>
                          </CardContent>
                        </Card>

                        {/* Your To-Do Items */}
                        {progression.buyer_todos?.length > 0 && (
                          <Card>
                            <CardHeader className="bg-amber-50 border-b border-amber-200">
                              <CardTitle className="flex items-center gap-2 text-amber-800">
                                <AlertCircle className="w-5 h-5" />
                                Your Action Items
                              </CardTitle>
                              <CardDescription className="text-amber-700">
                                Complete these items to keep the sale moving forward
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="p-4">
                              <div className="space-y-2">
                                {progression.buyer_todos.map((todo) => (
                                  <div
                                    key={todo.key}
                                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                                      todo.completed
                                        ? "bg-green-50 border-green-200"
                                        : "bg-white border-slate-200"
                                    }`}
                                    data-testid={`buyer-todo-${todo.key}`}
                                  >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      todo.completed ? "bg-green-500 text-white" : "bg-slate-200 text-slate-400"
                                    }`}>
                                      {todo.completed ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                                    </div>
                                    <span className={`font-medium ${todo.completed ? "text-green-700 line-through" : "text-slate-700"}`}>
                                      {todo.label}
                                    </span>
                                    {todo.completed && (
                                      <Badge className="bg-green-100 text-green-700 ml-auto">Done</Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Full Milestone List */}
                        <Card>
                          <CardHeader className="border-b bg-slate-50">
                            <CardTitle className="flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-blue-600" />
                              All Milestones
                            </CardTitle>
                            <CardDescription>Detailed view of all 18 progression steps</CardDescription>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="space-y-3">
                              {progression.milestones.map((milestone, index) => {
                                const isCompleted = milestone.status === "done";
                                const isInProgress = milestone.status === "in_progress";
                                const ownerLabel = milestone.owner?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "N/A";
                                
                                return (
                                  <div
                                    key={milestone.milestone_id || index}
                                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                                      isCompleted ? "bg-green-50 border-green-200" :
                                      isInProgress ? "bg-blue-50 border-blue-200" :
                                      "bg-white border-slate-200"
                                    }`}
                                    data-testid={`milestone-${milestone.key || index}`}
                                  >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                      isCompleted ? "bg-green-500 text-white" :
                                      isInProgress ? "bg-blue-500 text-white" :
                                      "bg-slate-200 text-slate-500"
                                    }`}>
                                      {isCompleted ? (
                                        <CheckCircle className="w-5 h-5" />
                                      ) : (
                                        <span className="font-bold text-sm">{index + 1}</span>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <p className={`font-medium ${
                                        isCompleted ? "text-green-700" :
                                        isInProgress ? "text-blue-700" :
                                        "text-slate-600"
                                      }`}>
                                        {milestone.label || milestone.name}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Responsible: {ownerLabel}
                                      </p>
                                    </div>
                                    <Badge className={
                                      isCompleted ? "bg-green-100 text-green-700" :
                                      isInProgress ? "bg-blue-100 text-blue-700" :
                                      "bg-slate-100 text-slate-600"
                                    }>
                                      {isCompleted ? "Completed" : isInProgress ? "In Progress" : "Pending"}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      </>
                    ) : (
                      /* Fallback to old milestone view */
                      <Card>
                        <CardHeader className="border-b bg-slate-50">
                          <CardTitle className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                            Purchase Progress
                          </CardTitle>
                          <CardDescription>Track your purchase milestones</CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <MilestoneItem label="Offer Accepted" completed={selectedPurchase.milestones?.offer_accepted} />
                            <MilestoneItem label="Memorandum Issued" completed={selectedPurchase.milestones?.memorandum_issued} />
                            <MilestoneItem label="Solicitor Instructed" completed={selectedPurchase.milestones?.solicitor_instructed} />
                            <MilestoneItem label="Mortgage Applied" completed={selectedPurchase.milestones?.mortgage_applied} />
                            <MilestoneItem label="Mortgage Approved" completed={selectedPurchase.milestones?.mortgage_approved} />
                            <MilestoneItem label="Survey Booked" completed={selectedPurchase.milestones?.survey_booked} />
                            <MilestoneItem label="Survey Complete" completed={selectedPurchase.milestones?.survey_complete} />
                            <MilestoneItem label="Searches Ordered" completed={selectedPurchase.milestones?.searches_ordered} />
                            <MilestoneItem label="Searches Received" completed={selectedPurchase.milestones?.searches_received} />
                            <MilestoneItem label="Enquiries Raised" completed={selectedPurchase.milestones?.enquiries_raised} />
                            <MilestoneItem label="Enquiries Answered" completed={selectedPurchase.milestones?.enquiries_answered} />
                            <MilestoneItem label="Exchange Ready" completed={selectedPurchase.milestones?.exchange_ready} />
                            <MilestoneItem label="Exchange Complete" completed={selectedPurchase.milestones?.exchange_complete} date={selectedPurchase.milestones?.exchange_date} />
                            <MilestoneItem label="Completion" completed={selectedPurchase.milestones?.completion_complete} date={selectedPurchase.milestones?.completion_date} />
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>

                  {/* Documents Tab */}
                  <TabsContent value="documents" className="mt-4 space-y-4">
                    {/* Required Documents */}
                    <Card>
                      <CardHeader className="border-b bg-slate-50">
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-blue-600" />
                          Required Documents
                        </CardTitle>
                        <CardDescription>Upload your verification documents</CardDescription>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* AML Verification */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-slate-600" />
                                <span className="font-medium">ID Verification (AML)</span>
                              </div>
                              {documents.find(d => d.type === 'aml_id') ? (
                                <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-3">Passport or driving licence</p>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                id="aml-upload"
                                onChange={(e) => handleDocumentUpload(e, 'aml_id')}
                                disabled={uploading}
                              />
                              <Label htmlFor="aml-upload" className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild disabled={uploading}>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {uploading ? "Uploading..." : "Upload"}
                                  </span>
                                </Button>
                              </Label>
                            </div>
                          </div>

                          {/* Proof of Address */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Home className="w-5 h-5 text-slate-600" />
                                <span className="font-medium">Proof of Address</span>
                              </div>
                              {documents.find(d => d.type === 'proof_of_address') ? (
                                <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-3">Utility bill or bank statement (within 3 months)</p>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                id="poa-upload"
                                onChange={(e) => handleDocumentUpload(e, 'proof_of_address')}
                                disabled={uploading}
                              />
                              <Label htmlFor="poa-upload" className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild disabled={uploading}>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {uploading ? "Uploading..." : "Upload"}
                                  </span>
                                </Button>
                              </Label>
                            </div>
                          </div>

                          {/* Proof of Funds */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Wallet className="w-5 h-5 text-slate-600" />
                                <span className="font-medium">Proof of Funds</span>
                              </div>
                              {documents.find(d => d.type === 'proof_of_funds') ? (
                                <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-3">Bank statement showing deposit funds</p>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                id="pof-upload"
                                onChange={(e) => handleDocumentUpload(e, 'proof_of_funds')}
                                disabled={uploading}
                              />
                              <Label htmlFor="pof-upload" className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild disabled={uploading}>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {uploading ? "Uploading..." : "Upload"}
                                  </span>
                                </Button>
                              </Label>
                            </div>
                          </div>

                          {/* Mortgage Offer */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-slate-600" />
                                <span className="font-medium">Mortgage Offer</span>
                              </div>
                              {documents.find(d => d.type === 'mortgage_offer') ? (
                                <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-400">Optional</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-3">Your formal mortgage offer letter</p>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                id="mortgage-upload"
                                onChange={(e) => handleDocumentUpload(e, 'mortgage_offer')}
                                disabled={uploading}
                              />
                              <Label htmlFor="mortgage-upload" className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild disabled={uploading}>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {uploading ? "Uploading..." : "Upload"}
                                  </span>
                                </Button>
                              </Label>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Uploaded Documents List */}
                    {documents.length > 0 && (
                      <Card>
                        <CardHeader className="border-b bg-slate-50">
                          <CardTitle>Uploaded Documents</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            {documents.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-blue-600" />
                                  <div>
                                    <p className="font-medium">{doc.name || doc.type}</p>
                                    <p className="text-sm text-slate-500">
                                      Uploaded {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                                    </p>
                                  </div>
                                </div>
                                {doc.url && (
                                  <Button variant="ghost" size="sm" asChild>
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                      <Download className="w-4 h-4" />
                                    </a>
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>

                  {/* Messages Tab */}
                  <TabsContent value="messages" className="mt-4">
                    <Card>
                      <CardHeader className="border-b bg-slate-50">
                        <CardTitle className="flex items-center gap-2">
                          <MessageSquare className="w-5 h-5 text-blue-600" />
                          Messages
                        </CardTitle>
                        <CardDescription>Communication with your estate agent</CardDescription>
                      </CardHeader>
                      <CardContent className="p-0">
                        {/* Messages List */}
                        <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
                          {messages.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                              <p>No messages yet</p>
                              <p className="text-sm">Send a message to your estate agent</p>
                            </div>
                          ) : (
                            messages.map((msg, idx) => (
                              <div 
                                key={idx} 
                                className={`flex ${msg.sender_type === 'buyer' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[70%] p-3 rounded-lg ${
                                  msg.sender_type === 'buyer' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white border'
                                }`}>
                                  <p className="text-sm">{msg.message}</p>
                                  <p className={`text-xs mt-1 ${msg.sender_type === 'buyer' ? 'text-blue-200' : 'text-slate-400'}`}>
                                    {new Date(msg.created_at).toLocaleString('en-GB')}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        
                        {/* Message Input */}
                        <div className="p-4 border-t bg-white">
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Type your message..."
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              className="min-h-[60px]"
                            />
                            <Button 
                              onClick={handleSendMessage} 
                              disabled={sendingMessage || !newMessage.trim()}
                              className="self-end"
                            >
                              {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Contacts Tab */}
                  <TabsContent value="contacts" className="mt-4">
                    <Card>
                      <CardHeader className="border-b bg-slate-50">
                        <CardTitle className="flex items-center gap-2">
                          <User className="w-5 h-5" />
                          Key Contacts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-600 font-medium mb-2">Your Estate Agent</p>
                            <p className="font-semibold text-slate-800">Goodacres Residential</p>
                            <div className="mt-2 space-y-1">
                              <p className="text-sm text-slate-600 flex items-center gap-2">
                                <Phone className="w-4 h-4" /> 01234 123456
                              </p>
                              <p className="text-sm text-slate-600 flex items-center gap-2">
                                <Mail className="w-4 h-4" /> info@goodacres.com
                              </p>
                            </div>
                          </div>
                          
                          {selectedPurchase.case?.negotiator && (
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-sm text-slate-500 font-medium mb-2">Sales Negotiator</p>
                              <p className="font-semibold text-slate-800">{selectedPurchase.case.negotiator}</p>
                            </div>
                          )}
                          
                          {selectedPurchase.case?.buyer_solicitor_name && (
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-sm text-slate-500 font-medium mb-2">Your Solicitor</p>
                              <p className="font-semibold text-slate-800">{selectedPurchase.case.buyer_solicitor_name}</p>
                            </div>
                          )}
                          
                          {selectedPurchase.case?.seller_solicitor_name && (
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-sm text-slate-500 font-medium mb-2">Seller&apos;s Solicitor</p>
                              <p className="font-semibold text-slate-800">{selectedPurchase.case.seller_solicitor_name}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-black text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Goodacres Residential. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BuyerDashboard;
